import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  getTournamentPlayers,
  searchPlayers,
  enrollPlayer,
  unenrollPlayer,
  quickRegisterAndEnrollPlayer,
} from '../../../services/tournament.service';
import QuickRegisterDialog from './QuickRegisterDialog';

export default function StepNormalPlayers({ torId, onNext, onBack, setAlertInfo }) {
  const [enrolledPlayers, setEnrolledPlayers] = useState([]);
  const [enrolledLoading, setEnrolledLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchEnrolledPlayers = async () => {
    if (!torId) return;
    setEnrolledLoading(true);
    try {
      const data = await getTournamentPlayers(torId);
      const sorted = (data || []).sort((a, b) => (b.elo ?? 0) - (a.elo ?? 0));
      setEnrolledPlayers(sorted);
    } catch (error) {
      console.error('Error fetching tournament players:', error);
      setAlertInfo({
        open: true,
        message: 'Error al obtener los jugadores inscritos.',
        severity: 'error',
      });
    } finally {
      setEnrolledLoading(false);
    }
  };

  useEffect(() => {
    fetchEnrolledPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [torId]);

  useEffect(() => {
    if (searchInputValue.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchPlayers(searchInputValue, torId);
        setSearchResults(results || []);
      } catch (error) {
        console.error('Error searching players:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInputValue, torId]);

  const handleEnrollPlayer = async (event, player) => {
    if (!player) return;
    try {
      setLoading(true);
      await enrollPlayer(torId, player.jug_id);
      setAlertInfo({
        open: true,
        message: `Jugador ${player.apellido_nombre} inscrito correctamente.`,
        severity: 'success',
      });
      setSearchInputValue('');
      setSearchResults([]);
      await fetchEnrolledPlayers();
    } catch (error) {
      console.error('Error enrolling player:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al inscribir al jugador.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnenrollPlayer = async (jugId, name) => {
    if (!torId || !jugId) return;
    try {
      setLoading(true);
      await unenrollPlayer(torId, jugId);
      setAlertInfo({
        open: true,
        message: `Inscripción de ${name} eliminada.`,
        severity: 'info',
      });
      await fetchEnrolledPlayers();
    } catch (error) {
      console.error('Error unenrolling player:', error);
      setAlertInfo({
        open: true,
        message: 'Error al desinscribir al jugador.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickRegisterSubmit = async (newPlayerData, resetForm) => {
    setLoading(true);
    try {
      const result = await quickRegisterAndEnrollPlayer(torId, newPlayerData);
      if (result && result.jug_id) {
        setAlertInfo({
          open: true,
          message: `Jugador ${result.apellido_nombre} registrado e inscrito exitosamente.`,
          severity: 'success',
        });
        setIsDialogOpen(false);
        resetForm();
        await fetchEnrolledPlayers();
      } else {
        throw new Error('No se pudo registrar el jugador');
      }
    } catch (error) {
      console.error('Error registering new player:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al registrar e inscribir al nuevo jugador.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#cbd5e1', mb: 2, fontWeight: 600 }}>
        Paso 2: Jugadores del Torneo
      </Typography>

      <Grid container spacing={3}>
        {/* Left Column: Search & Quick Add */}
        <Grid item xs={12} md={5}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              background: 'rgba(15, 23, 42, 0.5)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600, mb: 1 }}>
                Buscar e Inscribir
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                Busca un jugador por su apellido, nombre o número de DNI para inscribirlo.
              </Typography>

              <Autocomplete
                value={null}
                onChange={handleEnrollPlayer}
                inputValue={searchInputValue}
                onInputChange={(event, newInputValue) => {
                  setSearchInputValue(newInputValue);
                }}
                options={searchResults}
                getOptionLabel={(option) => `${option.apellido_nombre} (DNI: ${option.documento})`}
                loading={searchLoading}
                noOptionsText={searchInputValue.length < 2 ? 'Escribe al menos 2 letras...' : 'No se encontraron jugadores'}
                PaperComponent={({ children }) => (
                  <Paper sx={{ borderRadius: 0, background: '#1e293b', color: '#cbd5e1', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                    {children}
                  </Paper>
                )}
                ListboxProps={{
                  sx: {
                    backgroundColor: '#1e293b',
                    color: '#94a3b8',
                    '& .MuiAutocomplete-option': {
                      borderRadius: 0,
                      '&[aria-selected="true"]': {
                        backgroundColor: 'rgba(58, 110, 182, 0.25) !important',
                        color: '#cbd5e1',
                      },
                      '&[aria-selected="true"].Mui-focused': {
                        backgroundColor: 'rgba(58, 110, 182, 0.3) !important',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08) !important',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar Jugador"
                    placeholder="Ej: Pérez, Juan"
                    InputLabelProps={{ style: { color: '#94a3b8' } }}
                    inputProps={{ ...params.inputProps, style: { color: '#cbd5e1' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                        '&:hover fieldset': { borderColor: '#3a6eb6' },
                        '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', pt: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontWeight: 600, mb: 1 }}>
                ¿El jugador no está registrado?
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                Si no lo encuentras en la base de datos, puedes registrarlo rápidamente e inscribirlo de forma simultánea.
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => setIsDialogOpen(true)}
                sx={{
                  color: '#3a6eb6',
                  borderColor: 'rgba(58, 110, 182, 0.3)',
                  borderRadius: 0,
                  '&:hover': {
                    borderColor: '#3a6eb6',
                    background: 'rgba(58, 110, 182, 0.05)',
                  },
                }}
              >
                Registrar Nuevo Jugador
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Right Column: List of Enrolled Players */}
        <Grid item xs={12} md={7}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              background: 'rgba(15, 23, 42, 0.5)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: 0,
              minHeight: '320px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
                Jugadores Inscritos
              </Typography>
              <Typography variant="body2" sx={{ color: '#3a6eb6', fontWeight: 'bold' }}>
                Total: {enrolledPlayers.length}
              </Typography>
            </Box>

            {enrolledLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4 }}>
                <CircularProgress color="primary" />
              </Box>
            ) : enrolledPlayers.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4, flexDirection: 'column', textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                  No hay jugadores inscritos en este torneo aún.
                </Typography>
                <Typography variant="caption" sx={{ color: '#475569' }}>
                  Utiliza el buscador o registra uno nuevo para comenzar.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: '400px', flexGrow: 1, border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>#</TableCell>
                      <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Jugador</TableCell>
                      <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>DNI</TableCell>
                      <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>ELO</TableCell>
                      <TableCell align="right" sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enrolledPlayers.map((player, idx) => (
                      <TableRow key={player.jug_id || idx} hover sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.02) !important' } }}>
                        <TableCell sx={{ py: 0.5, color: '#94a3b8', borderColor: 'rgba(255, 255, 255, 0.05)' }}>{idx + 1}</TableCell>
                        <TableCell sx={{ py: 0.5, color: '#cbd5e1', fontWeight: 500, borderColor: 'rgba(255, 255, 255, 0.05)' }}>{player.apellido_nombre}</TableCell>
                        <TableCell sx={{ py: 0.5, color: '#94a3b8', borderColor: 'rgba(255, 255, 255, 0.05)' }}>{player.documento || '—'}</TableCell>
                        <TableCell sx={{ py: 0.5, color: '#94a3b8', borderColor: 'rgba(255, 255, 255, 0.05)' }}>{player.elo ?? 0}</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleUnenrollPlayer(player.jug_id, player.apellido_nombre)}
                            sx={{ p: 0.25, color: '#ef4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Step 2 Bottom Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            color: '#94a3b8',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 0,
            '&:hover': { borderColor: '#cbd5e1', background: 'rgba(255, 255, 255, 0.05)' },
          }}
        >
          Atrás
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={onNext}
          sx={{
            background: '#3a6eb6',
            borderRadius: 0,
            '&:hover': { background: '#2a5793' },
          }}
        >
          Siguiente
        </Button>
      </Box>

      {/* Quick Player Registration Dialog */}
      <QuickRegisterDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleQuickRegisterSubmit}
        loading={loading}
      />
    </Box>
  );
}
